import ElementController from "../base-controllers/ElementController.js";
/** Shared selection for planning and library results. Persistence is opt-in. */
class PickListSelection extends ElementController {
    constructor() {
        super(...arguments);
        this.selected = new Set();
        this.clearSelection = () => {
            this.selected.clear();
            for (const input of this.orderInputs)
                input.checked = false;
            this.persist();
            this.update();
        };
        this.selectAllChanged = () => {
            for (const input of this.orderInputs)
                input.checked = this.selectAllInput?.checked ?? false;
            this.orderChanged();
        };
        this.orderChanged = () => {
            for (const input of this.orderInputs) {
                if (input.checked && this.selected.size < 500)
                    this.selected.add(input.value);
                if (!input.checked)
                    this.selected.delete(input.value);
            }
            this.persist();
            this.update();
        };
    }
    init() {
        if (this.storageKey) {
            try {
                const stored = JSON.parse(sessionStorage.getItem(this.storageKey) || "[]");
                if (Array.isArray(stored))
                    this.selected = new Set(stored.filter((v) => typeof v === "string").slice(0, 500));
            }
            catch { /* Selection still works when storage is unavailable. */ }
        }
        this.update();
        this.observer = new MutationObserver(() => this.update());
        this.observer.observe(this.root, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-selection-remove"] });
    }
    destroy() { this.observer?.disconnect(); }
    get storageKey() { return this.root.dataset.selectionKey; }
    get orderInputs() {
        return Array.from(this.root.querySelectorAll("[data-pick-order], [data-selection-item]"));
    }
    get selectAllInput() {
        return this.root.querySelector("[data-pick-select-all], [data-selection-all]");
    }
    persist() {
        if (this.storageKey) {
            try {
                sessionStorage.setItem(this.storageKey, JSON.stringify([...this.selected]));
            }
            catch { /* Optional persistence. */ }
        }
    }
    update() {
        let removed = false;
        for (const id of this.root.dataset.selectionRemove?.split(",") ?? []) {
            if (this.selected.delete(id))
                removed = true;
        }
        if (removed)
            this.persist();
        const inputs = this.orderInputs;
        if (this.storageKey)
            for (const input of inputs)
                input.checked = this.selected.has(input.value);
        const selectedInputs = inputs.filter((input) => input.checked);
        const selectedCount = this.storageKey ? this.selected.size : selectedInputs.length;
        const selectAll = this.selectAllInput;
        if (selectAll) {
            selectAll.disabled = inputs.length === 0;
            selectAll.checked = inputs.length > 0 && selectedInputs.length === inputs.length;
            selectAll.indeterminate = selectedInputs.length > 0 && selectedInputs.length < inputs.length;
        }
        const count = this.root.querySelector("[data-pick-selection-count], [data-selection-count]");
        if (count && count.textContent !== String(selectedCount))
            count.textContent = String(selectedCount);
        const actions = this.root.querySelector("[data-pick-selection-actions], [data-selection-actions]");
        if (actions)
            actions.hidden = selectedCount === 0;
        const submit = this.root.querySelector("[data-pick-selection-actions] button[type='submit']");
        if (submit)
            submit.disabled = selectedCount === 0;
        for (const link of this.root.querySelectorAll("[data-selection-link]")) {
            const url = new URL(link.dataset.baseUrl, location.origin);
            for (const id of this.selected)
                url.searchParams.append("item_ids", id);
            if (link.hasAttribute("data-selection-return"))
                url.searchParams.set("return_to", location.pathname + location.search);
            if (link.tagName === "A")
                link.href = url.toString();
            if (link.dataset.selectionDialog) {
                const dialog = document.getElementById(link.dataset.selectionDialog);
                if (dialog)
                    dialog.dataset.contentUrl = url.toString();
            }
            link.hidden = link.hasAttribute("data-requires-two") && selectedCount !== 2;
        }
    }
}
PickListSelection.id = "PickListSelection";
PickListSelection.binds = {
    "[data-pick-order], [data-selection-item]": [["change", "orderChanged"]],
    "[data-pick-select-all], [data-selection-all]": [["change", "selectAllChanged"]],
    "[data-selection-clear]": [["click", "clearSelection"]],
};
export default PickListSelection;
//# sourceMappingURL=PickListSelection.js.map